import type { BusinessProfile } from '../api/client'
import { parseWholeNumber, priceInput } from '../catalog/format'

// GoalDraft is a goal before it is saved with a week.
export interface GoalDraft {
  take_home_target_pence: number
  monthly_costs_pence: number
  holiday_weeks_per_year: number
  business_profile: BusinessProfile
}

// GoalFields are the setup screen's inputs, as typed.
export interface GoalFields {
  target: string
  costs: string
  holidays: string
  profile: BusinessProfile | null
}

export const PROFILES: { id: BusinessProfile; label: string; blurb: string }[] = [
  { id: 'full_time', label: 'Full-time', blurb: 'Profit, premium pricing and loyal clients.' },
  { id: 'part_time', label: 'Part-time', blurb: 'The most from every hour you work.' },
  { id: 'parent_friendly', label: 'Around the family', blurb: 'School hours and fewer evenings.' },
  { id: 'side_business', label: 'Alongside a job', blurb: 'Evenings and weekends that add up.' },
  { id: 'starting', label: 'Just starting', blurb: 'Building a client base.' },
]

// parsePounds reads "3000", "3,000", "£3,000" or "3000.50" as pence, or null.
// Up to £999,999: a goal, not a price, so parsePrice's four digits are too few.
export function parsePounds(input: string): number | null {
  const match = /^(\d{1,3}(?:,\d{3})+|\d{1,6})(?:\.(\d{1,2}))?$/.exec(input.trim().replace(/^£\s*/, ''))
  if (!match) return null
  const pounds = Number(match[1].replace(/,/g, ''))
  const pence = match[2] ? Number(match[2].padEnd(2, '0')) : 0
  return pounds * 100 + pence
}

export function goalFields(goal: GoalDraft | null): GoalFields {
  if (!goal) return { target: '', costs: '', holidays: '', profile: null }
  return {
    target: priceInput(goal.take_home_target_pence),
    costs: priceInput(goal.monthly_costs_pence),
    holidays: String(goal.holiday_weeks_per_year),
    profile: goal.business_profile,
  }
}

export type GoalFieldErrors = Partial<Record<'target' | 'costs' | 'holidays' | 'profile', string>>

// checkGoalFields returns the message for each field that stops the goal, and
// {} when the goal is good to save.
export function checkGoalFields(f: GoalFields): GoalFieldErrors {
  const errors: GoalFieldErrors = {}
  const pence = parsePounds(f.target)
  if (!(pence !== null && pence > 0)) errors.target = 'Enter what you’d like to take home, like 3000.'
  if (f.costs.trim() !== '' && parsePounds(f.costs) === null) {
    errors.costs = 'Enter your costs, like 400, or leave it empty.'
  }
  const weeks = parseWholeNumber(f.holidays)
  if (!(weeks !== null && weeks <= 52)) errors.holidays = 'Enter the weeks you take off a year, 0 to 52.'
  if (!f.profile) errors.profile = 'Choose the one that sounds most like you.'
  return errors
}

// goalFromFields is the finished goal, or null while any field still fails.
export function goalFromFields(f: GoalFields): GoalDraft | null {
  if (Object.keys(checkGoalFields(f)).length > 0 || !f.profile) return null
  return {
    take_home_target_pence: parsePounds(f.target) ?? 0,
    monthly_costs_pence: f.costs.trim() === '' ? 0 : parsePounds(f.costs) ?? 0,
    holiday_weeks_per_year: parseWholeNumber(f.holidays) ?? 0,
    business_profile: f.profile,
  }
}
